import { useState, useEffect, useCallback, useRef } from 'react';
import axios, { AxiosRequestConfig, isAxiosError } from 'axios';
// import * as Sentry from '@sentry/browser';
import { WikimediaImage } from '../types/types';
import { StorageManager } from '../utils/storageManager';

const API_BASE_URL = process.env.REACT_APP_API_URL || ''; // Fallback to empty string for local proxy

// API URLs
const DAILY_API_URL = `${API_BASE_URL}/api/images`;

interface ImageFetchingOptions {
  mode: 'daily';
  selectedDate?: string;
}

// Helper function for retrying axios requests
const fetchWithRetry = async (
  url: string,
  config: AxiosRequestConfig = {},
  retries = 2,
  delay = 1500
) => {
  let attempt = 0;
  while (attempt <= retries) {
    try {
      const response = await axios.get(url, config);
      return response; // Success! Return the response
    } catch (error) {
      attempt++;
      // Check if it's an error worth retrying and if we have attempts left
      if (
        attempt > retries ||
        !isAxiosError(error) ||
        (error.response &&
          error.response.status < 500 &&
          error.response.status !== 499 &&
          error.response.status !== 408)
      ) {
        // Don't retry if max attempts reached, not an axios error,
        // or it's a client-side error (4xx, excluding 408/499 potentially)

        throw error; // Re-throw the error to be caught by the calling function
      }

      // Wait for the specified delay before the next attempt
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  // Should ideally not be reached if throw happens correctly, but satisfy TS
  throw new Error('Retry attempts exhausted.');
};

export const useImageFetching = ({
  mode,
  selectedDate,
}: ImageFetchingOptions) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [challenge, setChallenge] = useState<any | null>(null);
  const [currentImage, setCurrentImage] = useState<WikimediaImage | null>(null);

  // CRITICAL FIX: Initialize with saved round from localStorage
  const savedRoundLoaded = useRef(false);
  const [currentRound, setCurrentRoundState] = useState<number>(0);

  // CRITICAL: Get storage key helper
  const getStorageKey = useCallback(
    (key: string) => {
      const dateStr = selectedDate || StorageManager.getTodayDate();
      return `yearGuess_${key}_${dateStr}`;
    },
    [selectedDate]
  );

  // COMPLETE REWRITE: For daily challenge mode
  const fetchDailyChallenge = useCallback(async () => {
    setLoading(true);
    setError('');
    const dateToFetch = selectedDate || StorageManager.getTodayDateET(); // Use ET date

    try {
      // Get the date to fetch (selected date or today)
      const today = StorageManager.getTodayDate();

      // If this is today's challenge, clear any existing localStorage data first
      if (dateToFetch === today) {
        localStorage.removeItem(StorageManager.KEYS.COMPLETED_FLAG + today);
        localStorage.removeItem(StorageManager.KEYS.CHALLENGE_RESULTS + today);
        localStorage.removeItem(getStorageKey('gameState'));
        localStorage.removeItem(getStorageKey('revealedDigitsBackup'));
      }

      // Fetch the challenge directly
      const response = await fetchWithRetry(
        `${DAILY_API_URL}/daily-challenge/date/${dateToFetch}`
      );

      if (
        !response.data ||
        !response.data.images ||
        response.data.images.length === 0
      ) {
        // Clear localStorage data for this date since challenge doesn't exist
        const dateStr = selectedDate || StorageManager.getTodayDate();
        localStorage.removeItem(StorageManager.KEYS.COMPLETED_FLAG + dateStr);
        localStorage.removeItem(
          StorageManager.KEYS.CHALLENGE_RESULTS + dateStr
        );
        localStorage.removeItem(getStorageKey('lastRound'));
        localStorage.removeItem(getStorageKey('gameState'));
        localStorage.removeItem(getStorageKey('revealedDigitsBackup'));

        setError('No challenge available for this date');
        setLoading(false);
        return null;
      }

      // Set challenge data
      setChallenge(response.data);

      // Only try to restore saved round if we have a valid challenge
      const savedRoundString = localStorage.getItem(getStorageKey('lastRound'));
      let savedRound = 0;

      if (savedRoundString) {
        const parsed = parseInt(savedRoundString, 10);
        if (!isNaN(parsed) && parsed >= 0) {
          savedRound = parsed;
        }
      }

      // Set to saved round if valid
      const validRound = Math.min(savedRound, response.data.images.length - 1);
      setCurrentRoundState(validRound);
      setCurrentImage(response.data.images[validRound]);

      // Mark as loaded
      savedRoundLoaded.current = true;

      setLoading(false);
      return response.data;
    } catch (error: any) {
      // ---- START: Added Sentry Logging ----

      if (isAxiosError(error)) {
        if (error.response) {
          // Server responded with a status code outside 2xx range
          setError(
            `Failed to load challenge (Status: ${error.response.status})`
          );
        } else if (error.request) {
          // Request was made but no response received (network error, timeout, 499 likely ends here)
          // Axios often doesn't provide a status code for these network-level issues
          setError('Network error. Failed to load challenge.');
        } else {
          // Something happened in setting up the request
          setError('Error setting up request.');
        }
      } else {
        // Not an Axios error, could be something else
        setError('An unexpected error occurred loading the challenge.');
      }

      // Send the error to Sentry with useful tags
      // Sentry.captureException(error, {
      //    extra: { // Add extra context
      //      date_attempted: dateToFetch,
      //      hook_caller: 'fetchDailyChallenge'
      //    }
      // });
      // ---- END: Added Sentry Logging ----

      // Check if the error object indicates a 404 specifically
      const is404 = isAxiosError(error) && error.response?.status === 404;
      if (is404) {
        // Overwrite generic message if it was a 404
        setError('No challenge available for this date');
      }
      // Ensure error state is set based on the logic above before returning null

      setLoading(false);
      return null; // Indicate failure to the component using the hook
    }
  }, [selectedDate, getStorageKey]); // Keep dependencies minimal

  // CRITICAL FIX: Completely rewritten to be more reliable
  const setCurrentRound = useCallback(
    (round: number) => {
      // Use challenge state variable directly from hook scope

      const images = challenge?.images;

      // Perform validation checks
      if (!challenge) {
        return;
      }
      if (!images || !Array.isArray(images)) {
        return;
      }
      if (round < 0 || round >= images.length) {
        return;
      }

      // If validation passes:
      const imageToSet = images[round];

      // Update round state first
      setCurrentRoundState(round);

      // Then update image state
      setCurrentImage(imageToSet);

      // Save persistence (keep existing logic)
      try {
        // localStorage.setItem(`yearGuess_lastRound_${dateStr}`, round.toString()); // REMOVED this line
      } catch (e) {
        console.error('Failed to save lastRound', e);
      }
    },
    [challenge]
  );

  // CRITICAL FIX: Modified to ensure round consistency
  const nextRound = useCallback(() => {
    if (!challenge || !challenge.images) {
      return false;
    }

    const nextRoundIndex = currentRound + 1;

    if (nextRoundIndex >= challenge.images.length) {
      return false;
    }

    // Update round state
    setCurrentRoundState(nextRoundIndex);

    // Always update the image when changing round
    setCurrentImage(challenge.images[nextRoundIndex]);

    // Save to localStorage for persistence
    try {
      localStorage.setItem(
        getStorageKey('lastRound'),
        nextRoundIndex.toString()
      );
    } catch (e) {
      // Handle error silently
    }

    return true;
  }, [challenge, currentRound, getStorageKey]);

  // Check if this is the last round
  const isLastRound = useCallback(() => {
    if (!challenge) return true;
    return currentRound >= challenge.images.length - 1;
  }, [challenge, currentRound]);

  // CRITICAL: Reduce dependencies to avoid unnecessary refetches
  useEffect(() => {
    if (mode === 'daily') {
      fetchDailyChallenge();
    }
  }, [mode, selectedDate, fetchDailyChallenge]);

  // Submit score to API for daily challenge
  const submitScore = useCallback(
    async (
      score: number,
      roundGuesses?: Array<{ roundIndex: number; guessedYear: number }>
    ) => {
      const dateParam = selectedDate || StorageManager.getTodayDate();

      try {
        const response = await axios.post(
          `${DAILY_API_URL}/daily-challenge/submit`,
          {
            score,
            date: dateParam,
            guesses: roundGuesses,
          }
        );

        // Add Sentry message on success
        // Sentry.captureMessage('DC submission');

        return response.data.stats;
      } catch (err) {
        // Log the error to Sentry or console for debugging

        // Sentry.captureException(err, { extra: { score, dateParam } });
        return null;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [selectedDate]
  );

  return {
    loading,
    error,
    challenge, // Use the original name 'challenge' for consistency
    currentImage,
    currentRound,
    totalRounds: challenge ? challenge.images.length : 0, // Add this back
    isLastRound, // Add this back
    submitScore, // Add this back
    setCurrentRound,
    setCurrentImage, // Add this back
    nextRound,
    fetchDailyChallenge,
  };
};

export default useImageFetching;
